import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import { featureAccess } from "../../../permissions/access";
import {
  closeBdcStockDay,
  exportBdcStockDay,
  getBdcStockDay,
  getBdcStockReport,
  printBdcStockDay,
  reopenBdcStockDay,
  saveBdcStockDay,
} from "../../../services";
import { BdcStockDay, BdcStockReport } from "../../../types/bdc.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import BranchSelector from "../Branch/BranchSelector";
import { branchCodeOf } from "../Branch/branches";
import TestModeBanner from "../Branch/TestModeBanner";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { thaiDate, thaiDateTime, todayBkk } from "../format";
import { CARD_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import { openPdfResponse } from "../Invoice/pdf";
import { useBlobExport } from "../useBlobExport";
import {
  DcField,
  ItemField,
  StockDayDraft,
  buildSaveBody,
  draftErrors,
  emptyDraft,
  reportRangeError,
  withDcText,
  withItemText,
} from "./calc";
import CloseDialog from "./CloseDialog";
import DayView, { STOCK_TABLE_SX } from "./DayView";
import ReportView from "./ReportView";

type View = "day" | "report";

const DISCARD_PROMPT = "มีตัวเลขที่ยังไม่บันทึก — ทิ้งการแก้ไขนี้?";

const statusOf = (response: unknown): number | undefined =>
  (response as { response?: { status?: number } })?.response?.status;

/**
 * BDC · ขาด-เกิน — one sheet per BDC branch per ORDER date (board StockDay). The system fills
 * ยกมา, 7-11 สั่ง, the per-DC block and แคปให้บัญชี; หัวหน้า BDC (the GM) types โรงงาน,
 * รับโอน/โอนออก, เหลือ/ชำรุด/ขาย, saves, then ปิดวัน with ผู้จัดทำ + ผู้ตรวจสอบ. A closed day is
 * read-only until MASTER reopens it. Head office and เจ้าหน้าที่ BDC read. A second tab holds the
 * GM report over a range of closed days.
 */
export default function BdcStockDayScreen() {
  const role = useUserRole();
  const canWrite = featureAccess.bdcStockDayWrite(role);
  const canReopen = featureAccess.bdcStockDayReopen(role);
  const [params, setParams] = useSearchParams();
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);
  const { busy: exporting, run } = useBlobExport();
  const [printing, setPrinting] = useState(false);

  const view: View = params.get("view") === "report" ? "report" : "day";
  const today = todayBkk();
  const date = params.get("date") || today;
  const dateFrom = params.get("from") || `${today.slice(0, 8)}01`;
  const dateTo = params.get("to") || today;

  // One branch at a time: a viewer of one branch needs no pick; a viewer of two must pick.
  const companyId = branch.companyId ?? (branches.length === 1 ? branches[0].company_id : undefined);
  const needBranch = branchesReady && branches.length > 1 && !branch.companyId;
  const pickedBranch = branches.find((b) => b.company_id === companyId);

  const [day, setDay] = useState<BdcStockDay | null>(null);
  const [draft, setDraft] = useState<StockDayDraft>(emptyDraft);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [report, setReport] = useState<BdcStockReport | null>(null);
  const requestId = useRef(0);

  const saveBody = useMemo(() => (day ? buildSaveBody(day, draft, companyId) : null), [day, draft, companyId]);
  const errors = useMemo(() => (day ? draftErrors(day, draft) : { items: {}, dcs: {}, count: 0 }), [day, draft]);
  const dirty = saveBody !== null;
  const editable = canWrite && day?.status === "open";
  const rangeError = reportRangeError(dateFrom, dateTo);

  // ---- loading ----

  const loadDay = useCallback(async () => {
    if (!branchesReady || needBranch) return;
    const id = ++requestId.current;
    setLoading(true);
    // another day or branch: never leave the previous one on screen (or editable) while loading
    setDay((current) =>
      current && current.work_date === date && (!companyId || current.company_id === companyId) ? current : null,
    );
    try {
      const {
        data: { data },
      } = await getBdcStockDay(date, companyId);
      if (id !== requestId.current) return;
      setDay(data);
      setDraft(emptyDraft());
    } catch {
      if (id === requestId.current) setDay(null);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [branchesReady, needBranch, date, companyId]);

  const loadReport = useCallback(async () => {
    if (!branchesReady || needBranch || rangeError) return;
    const id = ++requestId.current;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getBdcStockReport(dateFrom, dateTo, companyId);
      if (id === requestId.current) setReport(data);
    } catch {
      if (id === requestId.current) setReport(null);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [branchesReady, needBranch, rangeError, dateFrom, dateTo, companyId]);

  useEffect(() => {
    if (view === "day") loadDay();
    else loadReport();
  }, [view, loadDay, loadReport]);

  // Leaving the page with typed figures asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ---- navigation (asks before throwing away a draft) ----

  const mayLeave = () => !dirty || window.confirm(DISCARD_PROMPT);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const pickDate = (key: "date" | "from" | "to") => (value: Dayjs | null) => {
    if (!value || !value.isValid()) return;
    if (key === "date" && !mayLeave()) return;
    update({ [key]: value.format("YYYY-MM-DD") });
  };

  const pickBranch = (value: string) => {
    if (!mayLeave()) return;
    branch.select(value);
  };

  const pickView = (next: View) => {
    if (next === view || !mayLeave()) return;
    setDraft(emptyDraft());
    update({ view: next === "report" ? "report" : null });
  };

  const openDay = (workDate: string) => update({ view: null, date: workDate });

  // ---- actions ----

  const onItemChange = (barcode: string, field: ItemField, text: string) =>
    setDraft((current) => withItemText(current, barcode, field, text));
  const onDcChange = (dcCode: string, field: DcField, text: string) =>
    setDraft((current) => withDcText(current, dcCode, field, text));

  const conflict = (error: unknown) => {
    // 409: someone closed / reopened the day meanwhile — show what the server has now
    if (statusOf(error) === 409) loadDay();
  };

  const save = async () => {
    if (!day || !saveBody || errors.count > 0) return;
    setSaving(true);
    try {
      const {
        data: { data },
      } = await saveBdcStockDay(day.work_date, saveBody);
      setDay(data);
      setDraft(emptyDraft());
    } catch (error) {
      conflict(error);
    } finally {
      setSaving(false);
    }
  };

  const close = async (preparedBy: string, checkedBy: string) => {
    if (!day) return;
    setClosing(true);
    try {
      const {
        data: { data },
      } = await closeBdcStockDay(day.work_date, {
        ...(companyId ? { company_id: companyId } : {}),
        prepared_by_name: preparedBy,
        checked_by_name: checkedBy,
      });
      setDay(data);
      setDraft(emptyDraft());
      setCloseOpen(false);
    } catch (error) {
      conflict(error);
    } finally {
      setClosing(false);
    }
  };

  const reopen = async () => {
    if (!day) return;
    setReopening(true);
    try {
      const {
        data: { data },
      } = await reopenBdcStockDay(day.work_date, companyId);
      setDay(data);
      setReopenOpen(false);
    } catch (error) {
      conflict(error);
    } finally {
      setReopening(false);
    }
  };

  const exportName = `bdc-stock-day-${pickedBranch ? branchCodeOf(pickedBranch) : "bdc"}-${date.replace(/-/g, "")}.xlsx`;
  const printDay = async (workDate: string) => {
    setPrinting(true);
    try {
      openPdfResponse(await printBdcStockDay(workDate, companyId), exportName.replace(/\.xlsx$/, ".pdf"));
    } catch {
      // the interceptor reports it (errorMsgByStatus)
    } finally {
      setPrinting(false);
    }
  };

  // ---- render ----

  const branchTitle = day?.branch_name || day?.branch_code || pickedBranch?.branch_name || "";

  return (
    <Box sx={{ padding: "8px 0", ...TABLE_SX, ...STOCK_TABLE_SX }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            ขาด-เกิน
          </Typography>
          {view === "day" && day ? (
            <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                {[branchTitle, `${day.weekday} ${thaiDate(day.work_date)} (วันสั่ง)`].filter(Boolean).join(" · ")} ·
              </Typography>
              {day.status === "closed" ? <Tag tone="ok">ปิดวันแล้ว</Tag> : <Tag tone="warn">ยังไม่ปิดวัน</Tag>}
              {!canWrite && (
                <Typography variant="body2" color="text.secondary">
                  · บัญชีนี้ดูได้อย่างเดียว
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {view === "day"
                ? "ต่อสาขา ต่อวันสั่ง · ระบบเติมยกมา ยอดสั่ง 7-11 และจำนวนร้าน · GM กรอกโรงงาน รับโอน/โอนออก และนับเหลือ/ชำรุด/ขาย"
                : "รายงาน GM ย้อนหลัง: โรงงาน vs ยอดสั่ง 7-11 vs เกิน (เฉพาะวันที่ปิดแล้ว)"}
            </Typography>
          )}
        </Box>
        {view === "day" && day && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              loading={exporting === "export"}
              disabled={dirty}
              title={dirty ? "บันทึกก่อน Export" : undefined}
              onClick={() => run("export", () => exportBdcStockDay(day.work_date, companyId), exportName)}
            >
              Export Excel (แบบฟอร์มเดิม)
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              loading={printing}
              disabled={dirty}
              title={dirty ? "บันทึกก่อนพิมพ์" : undefined}
              onClick={() => printDay(day.work_date)}
            >
              พิมพ์ A4
            </Button>
            {editable && (
              <>
                {dirty && (
                  <Button variant="text" disabled={saving} onClick={() => setDraft(emptyDraft())}>
                    ยกเลิกการแก้ไข
                  </Button>
                )}
                <Button variant="outlined" disabled={!dirty || errors.count > 0} loading={saving} onClick={save}>
                  บันทึก
                </Button>
                <Tooltip title={dirty ? "บันทึกตัวเลขที่แก้ก่อนปิดวัน" : ""}>
                  <span>
                    <Button variant="contained" disabled={dirty || saving} onClick={() => setCloseOpen(true)}>
                      ปิดวัน
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            {day.status === "closed" && canReopen && (
              <Button variant="outlined" color="warning" onClick={() => setReopenOpen(true)}>
                เปิดวันใหม่
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      <Stack gap={2}>
        <Card sx={{ ...CARD_SX, p: 1 }}>
          <Tabs value={view} onChange={(_e, next: View) => pickView(next)} sx={{ minHeight: 40, px: 1 }}>
            <Tab value="day" label="รายวัน" sx={{ minHeight: 40 }} />
            <Tab value="report" label="รายงาน GM" sx={{ minHeight: 40 }} />
          </Tabs>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center" sx={{ px: 1, pt: 1.5, pb: 0.5 }}>
              <BranchSelector branches={branches} value={branch.selected} onChange={pickBranch} allowAll={false} />
              {view === "day" ? (
                <>
                  <DatePicker
                    label="วันสั่ง"
                    value={dayjs(date)}
                    format="DD/MM/YYYY"
                    onChange={pickDate("date")}
                    slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {thaiDate(date)} · วันของไฟล์ ORDS (ไม่ใช่วันส่ง)
                  </Typography>
                </>
              ) : (
                <>
                  <DatePicker
                    label="ตั้งแต่"
                    value={dayjs(dateFrom)}
                    format="DD/MM/YYYY"
                    onChange={pickDate("from")}
                    slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
                  />
                  <DatePicker
                    label="ถึง"
                    value={dayjs(dateTo)}
                    format="DD/MM/YYYY"
                    onChange={pickDate("to")}
                    slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
                  />
                  <Typography variant="body2" color={rangeError ? "error" : "text.secondary"}>
                    {rangeError ?? "ไม่เกิน 92 วัน"}
                  </Typography>
                </>
              )}
            </Stack>
          </LocalizationProvider>
        </Card>

        <TestModeBanner branches={pickedBranch ? [pickedBranch] : []} />

        {needBranch && <Alert severity="info">เลือกสาขาก่อน — ขาด-เกินแยกกันต่อสาขา BDC</Alert>}

        {loading && <LinearProgress />}

        {view === "day" && day && !needBranch && (
          <>
            {day.status === "closed" && (
              <Alert severity="success" data-testid="bdc-stock-closed">
                ปิดวันแล้ว{day.closed_at ? ` ${thaiDateTime(day.closed_at)}` : ""}
                {day.closed_by_name ? ` โดย ${day.closed_by_name}` : ""} · ผู้จัดทำ {day.prepared_by_name || "-"} · ผู้ตรวจสอบ{" "}
                {day.checked_by_name || "-"} · แก้ไขไม่ได้
                {canReopen ? " — กด เปิดวันใหม่ เพื่อแก้" : " — ต้องให้ MASTER เปิดวันใหม่ก่อน"}
              </Alert>
            )}
            {dirty && (
              <Alert severity={errors.count > 0 ? "error" : "warning"}>
                {errors.count > 0
                  ? `มีช่องที่ต้องแก้ ${errors.count} ช่อง (ตัวเลขไม่ถูกต้อง หรือรับโอน/โอนออกยังไม่มีหมายเหตุ) — ตัวเลขด้านล่างเป็นค่าคำนวณก่อนบันทึก`
                  : "ยังไม่ได้บันทึก — ตัวเลขด้านล่างเป็นค่าคำนวณก่อนบันทึก ระบบจะคำนวณใหม่หลังบันทึก"}
              </Alert>
            )}
            <DayView
              day={day}
              draft={draft}
              errors={errors}
              editable={Boolean(editable)}
              onItemChange={onItemChange}
              onDcChange={onDcChange}
            />
            <CloseDialog
              open={closeOpen}
              day={day}
              busy={closing}
              onClose={() => setCloseOpen(false)}
              onConfirm={close}
            />
            <Dialog open={reopenOpen} onClose={reopening ? undefined : () => setReopenOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>เปิดวัน {thaiDate(day.work_date)} ใหม่</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  ตัวเลขระบบที่เก็บไว้ตอนปิดจะกลับมาคำนวณสด และแก้ไขได้อีกครั้ง · ยกมาของวันถัดไปจะไม่ใช้วันนี้จนกว่าจะปิดใหม่
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setReopenOpen(false)} disabled={reopening}>
                  ยกเลิก
                </Button>
                <Button variant="contained" color="warning" loading={reopening} onClick={reopen}>
                  เปิดวันใหม่
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {view === "report" && report && !needBranch && !rangeError && (
          <ReportView report={report} onOpenDay={openDay} />
        )}
      </Stack>
    </Box>
  );
}
